/**
 * ExtensionProviderInstance — Runtime instance for Extension Provider
 *
 * Manages IDE extensions (Cline, Roo Code, etc).
 * CDP webview discovery + agent stream collection moved here.
 */

import { flattenContent, type ProviderModule } from './contracts.js';
import type { ProviderInstance, ProviderState, ProviderEvent, InstanceContext, SessionModalState } from './provider-instance.js';
import { StatusMonitor } from './status-monitor.js';
import { buildPersistedProviderEffectMessage, normalizeProviderEffects } from './control-effects.js';
import { ChatHistoryWriter } from '../config/chat-history.js';
import type { ChatMessage } from '../types.js';
import { mergeProviderPatchState, resolveProviderStateSurface } from './provider-patch-state.js';
import { buildChatMessage, buildRuntimeSystemChatMessage, normalizeChatMessages, extractFinalSummaryFromMessages } from './chat-message-normalization.js';
import { getProviderSessionCapabilities, EXTENSION_PROVIDER_SESSION_CAPABILITIES_BASE } from './open-panel-support.js';

export class ExtensionProviderInstance implements ProviderInstance {
    readonly type: string;
    readonly category = 'extension' as const;

    private provider: ProviderModule;
    private context: InstanceContext | null = null;
    private settings: Record<string, any> = {};
    private events: ProviderEvent[] = [];

 // status
    private currentStatus: string = 'idle';
    private agentStreams: any[] = [];
    private messages: any[] = [];
    private prevMessageHashes = new Map<string, number>();
    private activeModal: any = null;
    private controlValues: Record<string, string | number | boolean> = {};
    private summaryMetadata: unknown = undefined;
    private appliedEffectKeys = new Set<string>();
    private runtimeMessages: Array<{ key: string; message: ChatMessage }> = [];
    private lastAgentStatus: string = 'idle';
    private generatingStartedAt: number = 0;
    private monitor: StatusMonitor;
    private historyWriter: ChatHistoryWriter;

 // meta
    private instanceId: string;
    private ideType: string = '';
    private chatId: string | null = null;
    private providerSessionId: string | null = null;
    private chatTitle: string | null = null;
    private agentName: string = '';
    private extensionId: string = '';

    constructor(provider: ProviderModule) {
        this.type = provider.type;
        this.provider = provider;
        this.instanceId = crypto.randomUUID();
        this.monitor = new StatusMonitor();
        this.historyWriter = new ChatHistoryWriter();
    }

 // ─── Lifecycle ──────────────────────────────────

    async init(context: InstanceContext): Promise<void> {
        this.context = context;
        this.settings = context.settings || {};
        this.monitor.updateConfig({
            approvalAlert: this.settings.approvalAlert !== false,
            noProgressAlert: (this.settings.noProgressAlert ?? this.settings.longGeneratingAlert) !== false,
            noProgressThresholdSec: this.settings.noProgressThresholdSec ?? this.settings.longGeneratingThresholdSec ?? 180,
        });
    }

    async onTick(): Promise<void> {
 // Extension gets data pushed from IDE's CDP agent stream
 // Needed when direct stream collection via CDP is possible
        if (!this.context?.cdp?.isConnected) return;

 // Agent stream collect (CDP discoverAgentWebviews etc)
 // Currently handled separately by agent-stream-manager only
 // Can be moved here in the future
    }

    getState(): ProviderState {
        const surface = resolveProviderStateSurface({
            summaryMetadata: this.summaryMetadata as any,
            controlValues: this.controlValues,
        })

        return {
            type: this.type,
            name: this.provider.name,
            category: 'extension',
            status: this.currentStatus as ProviderState['status'],
            providerSessionId: this.providerSessionId || this.chatId || undefined,
            sessionCapabilities: getProviderSessionCapabilities(this.provider, EXTENSION_PROVIDER_SESSION_CAPABILITIES_BASE),
            activeChat: (this.messages.length > 0 || this.runtimeMessages.length > 0) ? {
                id: this.providerSessionId || this.chatId || this.instanceId,
                title: this.chatTitle || this.agentName || this.provider.name,
                status: this.currentStatus,
                messages: this.mergeConversationMessages(this.messages),
                activeModal: this.activeModal,
                inputContent: '',
            } : null,
            controlValues: surface.controlValues,
            providerControls: this.provider.controls,
            summaryMetadata: surface.summaryMetadata as any,
            agentStreams: this.agentStreams,
            instanceId: this.instanceId,
            lastUpdated: Date.now(),
            settings: this.settings,
            pendingEvents: this.flushEvents(),
        };
    }

    getSessionModalState(sessionId?: string): SessionModalState | null {
        if (sessionId && sessionId !== this.instanceId) return null;
        return {
            id: this.instanceId,
            status: this.currentStatus,
            title: this.chatTitle || this.agentName || this.provider.name,
            activeModal: this.activeModal,
        };
    }

    onEvent(event: string, data?: any): void {
        if (event === 'stream_update') {
 // Reflect data collected from agent-stream-manager
            if (data?.streams) this.agentStreams = data.streams;
            if (data?.messages) this.messages = this.assignReceivedAt(data.messages);
            if (data?.activeModal !== undefined) this.activeModal = data.activeModal;
            const patchedState = mergeProviderPatchState({
                providerControls: this.provider.controls,
                data,
                currentControlValues: this.controlValues,
                currentSummaryMetadata: this.summaryMetadata,
            });
            this.controlValues = patchedState.controlValues;
            this.summaryMetadata = patchedState.summaryMetadata;
            const nextProviderSessionId = typeof data?.providerSessionId === 'string' && data.providerSessionId.trim()
                ? data.providerSessionId.trim()
                : typeof data?.sessionId === 'string' && data.sessionId.trim()
                    ? data.sessionId.trim()
                    : '';
            if (nextProviderSessionId) {
                this.providerSessionId = nextProviderSessionId;
                this.chatId = nextProviderSessionId;
            }
            if (typeof data?.title === 'string' && data.title.trim()) this.chatTitle = data.title;
            if (typeof data?.agentName === 'string' && data.agentName.trim()) this.agentName = data.agentName;
            if (typeof data?.extensionId === 'string' && data.extensionId.trim()) this.extensionId = data.extensionId;
            if (data?.status) {
                const newStatus = data.status;
                this.detectTransition(newStatus, data);
                this.currentStatus = newStatus;
            }
        } else if (event === 'stream_reset') {
            this.resetStreamState();
        } else if (event === 'extension_connected') {
            this.ideType = data?.ideType || '';
        } else if (event === 'provider_state_patch' && data && typeof data === 'object') {
            this.applyProviderResponse(data, { phase: 'immediate' });
 // Maintain instanceId UUID — do not overwrite
        }
    }

    dispose(): void {
        this.agentStreams = [];
        this.messages = [];
        this.prevMessageHashes.clear();
        this.monitor.reset();
        this.appliedEffectKeys.clear();
        this.runtimeMessages = [];
    }

    updateSettings(newSettings: Record<string, any>): void {
        // Merge (not replace): a key omitted from newSettings preserves its existing
        // value, a key present (even false) overrides. Mirrors cli/ide-provider-instance
        // so partial updates do not wipe launch-time settings (e.g. autoApprove) while a
        // full settings object still applies explicit values.
        this.settings = { ...this.settings, ...newSettings };
        this.monitor.updateConfig({
            approvalAlert: this.settings.approvalAlert !== false,
            noProgressAlert: (this.settings.noProgressAlert ?? this.settings.longGeneratingAlert) !== false,
            noProgressThresholdSec: this.settings.noProgressThresholdSec ?? this.settings.longGeneratingThresholdSec ?? 180,
        });
    }

 /** Query UUID instanceId */
    getInstanceId(): string {
        return this.instanceId;
    }

 // ─── status transition detect ──────────────────────────────
    private detectTransition(newStatus: string, data: any): void {
        const now = Date.now();
        const agentStatus = (newStatus === 'streaming' || newStatus === 'generating') ? 'generating'
            : newStatus === 'waiting_approval' ? 'waiting_approval'
            : 'idle';
        const lastMsg = Array.isArray(data?.messages) && data.messages.length > 0
            ? data.messages[data.messages.length - 1]
            : null;
        const progressFingerprint = agentStatus === 'generating'
            ? `${lastMsg?.role || ''}:${typeof lastMsg?.content === 'string' ? lastMsg.content : JSON.stringify(lastMsg?.content || '')}`.slice(-2000)
            : undefined;

        const previousStatus = this.lastAgentStatus;
        if (agentStatus !== this.lastAgentStatus) {
            if (this.lastAgentStatus === 'idle' && agentStatus === 'generating') {
                this.generatingStartedAt = now;
                this.pushEvent({
                    event: 'agent:generating_started',
                    chatTitle: this.resolveChatTitle(data),
                    timestamp: now,
                    ideType: this.ideType || this.type,
                    agentType: this.type,
                    agentName: this.agentName || this.provider.name,
                    extensionId: this.extensionId || this.type,
                });
            } else if (agentStatus === 'waiting_approval') {
                if (!this.generatingStartedAt) this.generatingStartedAt = now;
                this.pushEvent({
                    event: 'agent:waiting_approval',
                    chatTitle: this.resolveChatTitle(data),
                    timestamp: now,
                    ideType: this.ideType || this.type,
                    agentType: this.type,
                    agentName: this.agentName || this.provider.name,
                    extensionId: this.extensionId || this.type,
                    modalMessage: data?.activeModal?.message,
                    modalButtons: data?.activeModal?.buttons,
                });
            } else if (agentStatus === 'idle' && (this.lastAgentStatus === 'generating' || this.lastAgentStatus === 'waiting_approval')) {
                const duration = this.generatingStartedAt ? Math.round((now - this.generatingStartedAt) / 1000) : 0;
                this.pushEvent({
                    event: 'agent:generating_completed',
                    chatTitle: this.resolveChatTitle(data),
                    duration,
                    timestamp: now,
                    ideType: this.ideType || this.type,
                    agentType: this.type,
                    agentName: this.agentName || this.provider.name,
                    extensionId: this.extensionId || this.type,
                    finalSummary: extractFinalSummaryFromMessages(data?.messages),
                });
                this.generatingStartedAt = 0;
            }
            this.lastAgentStatus = agentStatus;
        }

        this.applyProviderResponse(data, {
            phase: (agentStatus === 'idle' && (previousStatus === 'generating' || previousStatus === 'waiting_approval'))
                ? 'turn_completed'
                : 'immediate',
        });

 // Monitor check (cooldown based notification) — keep monitor events (no_progress etc)
        const agentKey = `${this.type}:ext`;
        const approvalPending = agentStatus === 'waiting_approval';
        const monitorEvents = this.monitor.check(agentKey, agentStatus, now, progressFingerprint, approvalPending);
        for (const me of monitorEvents) {
            this.pushEvent({ event: me.type, agentKey: me.agentKey, message: me.message, elapsedSec: me.elapsedSec, timestamp: me.timestamp });
        }
    }

    private pushEvent(event: ProviderEvent): void {
        this.events.push(event);
    }

    private applyProviderResponse(data: any, options: { phase: 'immediate' | 'turn_completed' }): void {
        if (!data || typeof data !== 'object') return;

        const patchedState = mergeProviderPatchState({
            providerControls: this.provider.controls,
            data,
            currentControlValues: this.controlValues,
            currentSummaryMetadata: this.summaryMetadata,
        });
        this.controlValues = patchedState.controlValues;
        this.summaryMetadata = patchedState.summaryMetadata;

        const effects = normalizeProviderEffects(data);
        for (const effect of effects) {
            const effectWhen = effect.when || 'immediate';
            if (effectWhen === 'turn_completed' && options.phase !== 'turn_completed') continue;
            if (effectWhen === 'immediate' && options.phase === 'turn_completed') continue;

            const effectKey = this.getEffectDedupKey(effect);
            if (this.appliedEffectKeys.has(effectKey)) continue;
            this.appliedEffectKeys.add(effectKey);

            if (effect.persist !== false) {
                const persistedMessage = buildPersistedProviderEffectMessage(effect);
                if (persistedMessage) this.appendRuntimeMessage(persistedMessage, effectKey);
            }

            if (effect.type === 'message' && effect.message) {
                this.pushEvent({
                    event: 'provider:message',
                    timestamp: Date.now(),
                    content: typeof effect.message.content === 'string' ? effect.message.content : JSON.stringify(effect.message.content),
                    role: effect.message.role || 'system',
                    kind: effect.message.kind,
                    senderName: effect.message.senderName,
                });
            } else if (effect.type === 'toast' && effect.toast) {
                this.pushEvent({
                    event: 'provider:toast',
                    effectId: effect.id || effectKey,
                    timestamp: Date.now(),
                    message: effect.toast.message,
                    level: effect.toast.level || 'info',
                });
            } else if (effect.type === 'notification' && effect.notification) {
                this.pushEvent({
                    event: 'provider:notification',
                    effectId: effect.id || effectKey,
                    timestamp: Date.now(),
                    title: effect.notification.title,
                    message: effect.notification.body,
                    content: typeof effect.notification.bubbleContent === 'string'
                        ? effect.notification.bubbleContent
                        : effect.notification.body,
                    level: effect.notification.level || 'info',
                    channels: effect.notification.channels || ['toast'],
                    preferenceKey: effect.notification.preferenceKey,
                });
            }
        }
    }

    private appendRuntimeSystemMessage(content: string, dedupKey: string, receivedAt = Date.now()): void {
        this.appendRuntimeMessage(buildRuntimeSystemChatMessage({
            content,
            receivedAt,
            timestamp: receivedAt,
        }), dedupKey);
    }

    private appendRuntimeMessage(message: ChatMessage, dedupKey: string): void {
        const normalizedMessage = buildChatMessage({
            ...message,
            receivedAt: typeof message.receivedAt === 'number' ? message.receivedAt : (message.timestamp || Date.now()),
            timestamp: typeof message.timestamp === 'number' ? message.timestamp : (message.receivedAt || Date.now()),
        } as ChatMessage);
        const normalizedContent = typeof normalizedMessage.content === 'string'
            ? normalizedMessage.content.trim()
            : flattenContent(normalizedMessage.content).trim();
        if (!normalizedContent && (!Array.isArray(normalizedMessage.content) || normalizedMessage.content.length === 0)) return;
        if (this.runtimeMessages.some((entry) => entry.key === dedupKey)) return;

        this.runtimeMessages.push({
            key: dedupKey,
            message: normalizedMessage,
        });

        if (normalizedContent) {
            this.historyWriter.appendNewMessages(
                this.type,
                [{
                    role: normalizedMessage.role,
                    senderName: normalizedMessage.senderName,
                    kind: normalizedMessage.kind,
                    content: normalizedContent,
                    receivedAt: normalizedMessage.receivedAt || normalizedMessage.timestamp,
                    historyDedupKey: dedupKey,
                }],
                this.chatTitle || this.agentName || this.provider.name,
                this.instanceId,
                this.chatId || this.instanceId,
            );
        }
    }

    private buildSyntheticTurnKey(message: any, occurrence: number): string {
        const role = typeof message?.role === 'string' ? message.role : '';
        const kind = typeof message?.kind === 'string' ? message.kind : '';
        const senderName = typeof message?.senderName === 'string' ? message.senderName : '';
        const content = flattenContent(message?.content)
            .replace(/\s+/g, ' ')
            .trim()
            .slice(0, 500);
        return `${role}|${kind}|${senderName}|${content}|${occurrence}`;
    }

    /**
     * Assign stable receivedAt / synthetic _turnKey to extension messages.
     * Same transcript should keep the same identity across polling cycles and
     * stream resets, while repeated identical text later in the transcript still
     * produces a distinct completion marker via the occurrence suffix.
     */
    private assignReceivedAt(messages: any[]): any[] {
        const now = Date.now();
        const nextHashes = new Map<string, number>();
        const occurrenceByBaseKey = new Map<string, number>();

        for (const msg of messages) {
            const explicitTurnKey = typeof msg?._turnKey === 'string' && msg._turnKey.trim()
                ? msg._turnKey.trim()
                : '';
            const explicitId = typeof msg?.id === 'string' && msg.id.trim()
                ? `id:${msg.id.trim()}`
                : '';
            const explicitIndex = typeof msg?.index === 'number' && Number.isFinite(msg.index)
                ? `idx:${msg.index}`
                : '';
            const baseKey = explicitTurnKey || explicitId || explicitIndex || `${msg?.role || ''}:${flattenContent(msg?.content || '').slice(0, 500)}`;
            const occurrence = (occurrenceByBaseKey.get(baseKey) || 0) + 1;
            occurrenceByBaseKey.set(baseKey, occurrence);
            const syntheticTurnKey = explicitTurnKey || explicitId || explicitIndex || this.buildSyntheticTurnKey(msg, occurrence);
            if (!explicitTurnKey && !explicitId && !explicitIndex) {
                msg._turnKey = syntheticTurnKey;
            }

            const prevTime = this.prevMessageHashes.get(syntheticTurnKey);
            msg.receivedAt = prevTime || now;
            nextHashes.set(syntheticTurnKey, msg.receivedAt);
        }

        this.prevMessageHashes = nextHashes;
        return normalizeChatMessages(messages);
    }

    private mergeConversationMessages(messages: any[]): ChatMessage[] {
        if (this.runtimeMessages.length === 0) return normalizeChatMessages(messages);
        return normalizeChatMessages([...messages, ...this.runtimeMessages.map((entry) => entry.message)]
            .map((message, index) => ({ message, index }))
            .sort((a, b) => {
                const aTime = a.message.receivedAt || a.message.timestamp || 0;
                const bTime = b.message.receivedAt || b.message.timestamp || 0;
                if (aTime !== bTime) return aTime - bTime;
                return a.index - b.index;
            })
            .map((entry) => entry.message));
    }

    private getPersistedEffectContent(effect: { type: string; message?: { content?: unknown }; toast?: { message?: string }; notification?: { title?: string; body?: string; bubbleContent?: unknown } }): string | null {
        if (effect.type === 'message') {
            return typeof effect.message?.content === 'string'
                ? effect.message.content
                : JSON.stringify(effect.message?.content || '');
        }
        if (effect.type === 'toast') {
            return effect.toast?.message || null;
        }
        if (effect.type === 'notification') {
            if (typeof effect.notification?.bubbleContent === 'string') return effect.notification.bubbleContent;
            if (typeof effect.notification?.title === 'string' && effect.notification.title.trim()) {
                return `${effect.notification.title}\n${effect.notification.body || ''}`.trim();
            }
            return effect.notification?.body || null;
        }
        return null;
    }

    private getEffectDedupKey(effect: { id?: string; type: string; message?: { content?: unknown }; toast?: { message?: string }; notification?: { title?: string; body?: string } }): string {
        if (effect.id) return `provider_effect:${effect.id}`;
        if (effect.type === 'message') {
            return `provider_effect:message:${typeof effect.message?.content === 'string' ? effect.message.content : JSON.stringify(effect.message?.content || '')}`;
        }
        if (effect.type === 'notification') {
            return `provider_effect:notification:${effect.notification?.title || ''}:${effect.notification?.body || ''}`;
        }
        return `provider_effect:toast:${effect.toast?.message || ''}`;
    }

    private flushEvents(): ProviderEvent[] {
        const events = [...this.events];
        this.events = [];
        return events;
    }

    private resolveChatTitle(data: any): string {
        const title = typeof data?.title === 'string' && data.title.trim()
            ? data.title.trim()
            : this.chatTitle;
        return title || this.agentName || this.provider.name;
    }

    private resetStreamState(): void {
        if (this.currentStatus !== 'idle') {
            this.detectTransition('idle', {
                title: this.chatTitle,
                agentName: this.agentName,
                extensionId: this.extensionId,
                messages: this.messages,
            });
        }
        this.agentStreams = [];
        this.messages = [];
        this.prevMessageHashes.clear();
        this.activeModal = null;
        this.controlValues = {};
        this.currentStatus = 'idle';
        this.chatId = null;
        this.providerSessionId = null;
        this.chatTitle = null;
        this.agentName = '';
        this.extensionId = '';
        this.lastAgentStatus = 'idle';
        this.generatingStartedAt = 0;
        this.monitor.reset();
    }
}
